import { useEffect, useMemo, useState } from 'react'
import { Button, Collection, Tree, TreeItem, TreeItemContent, type Key } from 'react-aria-components'
import type { Folder } from '@bloo-team/api-client'
import { filterFolderTree, folderIds } from '../../lib/detection-list'

/**
 * The Detections library's folder rail (#205): the directory/list tree
 * under an "All" root. Props in, events out — the page owns the selection
 * (URL `folder` = folder id) and the query. Built on react-aria-components
 * Tree (keyboard: arrows move/expand, Enter/Space selects); skin is the
 * design system's `bui-tree*` classes.
 */

const ALL = '__all__'

interface Node {
  id: string
  name: string
  children: Node[]
}

/** Ids of every ancestor of `id`, root first; null when not in the tree. */
function ancestorsOf(nodes: readonly Folder[], id: string, trail: string[] = []): string[] | null {
  for (const n of nodes) {
    if (n.id === id) return trail
    const hit = ancestorsOf(n.children, id, [...trail, n.id])
    if (hit) return hit
  }
  return null
}

export interface FolderTreeProps {
  folders: Folder[]
  /** Selected folder id; null = All. */
  selectedId: string | null
  onSelect: (id: string | null) => void
  isPending?: boolean
  isError?: boolean
}

export function FolderTree({ folders, selectedId, onSelect, isPending, isError }: FolderTreeProps) {
  const [find, setFind] = useState('')
  const [expanded, setExpanded] = useState<Set<Key>>(() => new Set<Key>([ALL]))
  // A selected folder (deep link, or folders arriving after the URL) is
  // always revealed: open its ancestors, keep whatever else is open.
  useEffect(() => {
    const trail = selectedId ? ancestorsOf(folders, selectedId) : null
    if (trail?.length) setExpanded((prev) => new Set<Key>([...prev, ...trail]))
  }, [folders, selectedId])
  const visible = useMemo(() => filterFolderTree(folders, find), [folders, find])
  const items = useMemo<Node[]>(() => {
    const toNode = (f: Folder): Node => ({ id: f.id, name: f.name, children: f.children.map(toNode) })
    return [{ id: ALL, name: 'All', children: visible.map(toNode) }]
  }, [visible])
  // While finding, every surviving folder is open so matches are on screen.
  const expandedKeys = find.trim() ? new Set<Key>([ALL, ...folderIds(visible)]) : expanded

  return (
    <nav className="det-rail" aria-label="Detection folders">
      <span className="hud-eyebrow">folders</span>
      <input
        type="search"
        className="det-find mono"
        aria-label="Find folder"
        placeholder="find folder…"
        value={find}
        onChange={(e) => setFind(e.target.value)}
      />
      {isPending ? (
        <p className="hud-unit" role="status">Loading folders…</p>
      ) : isError ? (
        <div className="bui-alert bui-alert-error" role="alert">
          <span className="bui-alert-mark">✕</span>
          <span>Folders could not be loaded.</span>
        </div>
      ) : (
        <Tree
          aria-label="Folders"
          className="bui-tree det-tree"
          items={items}
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={[selectedId ?? ALL]}
          onSelectionChange={(keys) => {
            if (keys === 'all') return
            const [key] = [...keys]
            if (key !== undefined) onSelect(key === ALL ? null : String(key))
          }}
          expandedKeys={expandedKeys}
          onExpandedChange={(keys) => {
            if (!find.trim()) setExpanded(new Set(keys))
          }}
        >
          {function renderNode(node: Node) {
            return (
              <TreeItem id={node.id} textValue={node.name} className="bui-tree-row">
                <TreeItemContent>
                  {({ hasChildItems, isExpanded, level }) => (
                    <>
                      <span
                        className="det-tree-indent"
                        aria-hidden="true"
                        style={{ width: `calc(var(--bloo-space-4) * ${level - 1})` }}
                      />
                      {hasChildItems ? (
                        <Button
                          slot="chevron"
                          className="bui-tree-chev"
                          data-open={isExpanded ? 'true' : undefined}
                          // RAC labels the chevron by this + the row: "Expand Native".
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          ›
                        </Button>
                      ) : (
                        <span className="bui-tree-chev" aria-hidden="true" />
                      )}
                      <span className="det-tree-name">{node.name}</span>
                    </>
                  )}
                </TreeItemContent>
                <Collection items={node.children}>{renderNode}</Collection>
              </TreeItem>
            )
          }}
        </Tree>
      )}
    </nav>
  )
}
